import { email, required } from "ra-core";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { TextInput } from "@/components/admin/text-input";

import { CustomFieldsInputs } from "../customFields/CustomFields";
import type { PartnerCategory, Sale } from "../types";
import { usePartnerCategoryChoices, usePartnerTypes } from "./partnerTypes";

/** A cleared select gives "": store null instead. */
const emptyToNull = (value: string) => (value === "" ? null : value);

const saleName = (sale: Sale) => `${sale.first_name} ${sale.last_name}`;

export const PartnerInputs = () => {
  const categoryChoices = usePartnerCategoryChoices();
  const category = useWatch<{ category?: PartnerCategory }>({
    name: "category",
  });
  const types = usePartnerTypes(category);
  const { getValues, setValue } = useFormContext();

  // A type belongs to one category: drop it when the category changes.
  useEffect(() => {
    const type = getValues("type");
    if (type && !types.some((item) => item.value === type)) {
      setValue("type", null, { shouldDirty: true });
    }
  }, [types, getValues, setValue]);

  return (
    <div className="flex flex-col gap-4">
      <TextInput
        autoFocus
        source="name"
        validate={required()}
        helperText={false}
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <SelectInput
          source="category"
          choices={categoryChoices}
          optionText="label"
          optionValue="value"
          validate={required()}
          helperText={false}
        />
        <SelectInput
          source="type"
          choices={types}
          optionText="label"
          optionValue="value"
          emptyText="crm.common.to_define"
          parse={emptyToNull}
          helperText={false}
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextInput source="phone" helperText={false} />
        <TextInput source="email" validate={email()} helperText={false} />
      </div>
      <TextInput source="organisation" helperText={false} />
      {category === "staff" ? (
        <ReferenceInput
          source="linked_sales_id"
          reference="sales"
          filter={{ "disabled@neq": true }}
          sort={{ field: "last_name", order: "ASC" }}
        >
          <SelectInput
            label="resources.partners.fields.linked_sales_id"
            optionText={saleName}
            emptyText="resources.partners.no_linked_account"
            parse={emptyToNull}
            helperText="resources.partners.linked_sales_help"
          />
        </ReferenceInput>
      ) : null}
      <TextInput source="background" multiline helperText={false} />
      <CustomFieldsInputs resource="partners" />
    </div>
  );
};
